import { repositoryPos } from "../../_base/_const/_constVar";
import { MessageResponse } from "../../model/MessageResponse";

/**
 * Bốn loại phiếu kho của luồng Dịch vụ sửa chữa, tương ứng 4 cột mã phiếu
 * trên màn "Bảng kê hàng dịch vụ".
 */
export type ServiceRepairVoucherType =
  | "NHAP_KHO_BAO_HANH"
  | "NHAP_KHO_BAO_HANH_NCC"
  | "XUAT_KHO_BAO_HANH_NCC"
  | "XUAT_KHO_BAO_HANH";

export const SERVICE_REPAIR_VOUCHER_TITLE: Record<
  ServiceRepairVoucherType,
  string
> = {
  NHAP_KHO_BAO_HANH: "Nhập kho bảo hành",
  NHAP_KHO_BAO_HANH_NCC: "Nhập kho bảo hành NCC",
  XUAT_KHO_BAO_HANH_NCC: "Xuất kho bảo hành NCC",
  XUAT_KHO_BAO_HANH: "Xuất trả khách",
};

/** Phiếu nhập dùng InwardController, phiếu xuất dùng OutwardController. */
const isInward = (type: ServiceRepairVoucherType) => type.startsWith("NHAP_");

/**
 * Gọi API chi tiết phiếu kho của luồng Dịch vụ sửa chữa.
 * Trả về null khi không lấy được để phía gọi tự hiển thị thông báo.
 */
export const getServiceRepairVoucherDetail = async (
  transactionCode: string,
  type: ServiceRepairVoucherType,
): Promise<any | null> => {
  if (!transactionCode) return null;

  const route = isInward(type)
    ? "/api/v1/Inward/detail-service-repair-inward"
    : "/api/v1/Outward/detail-service-repair-outward";

  const res = await repositoryPos.get<MessageResponse<any>>(
    `${route}?transactionCode=${encodeURIComponent(transactionCode)}`,
  );

  if (res?.success && res.data) return res.data;
  return null;
};

/**
 * Chuyển danh sách serial từ API chi tiết về đúng shape mà các bảng trên
 * màn tạo mới đang dùng, để tái sử dụng nguyên vẹn phần hiển thị.
 */
export const mapDetailSerialsToRows = (detail: any): any[] => {
  if (!detail?.serials?.length) return [];

  return detail.serials.map((line: any, index: number) => ({
    // MantineReactTable dùng getRowId = row.id nên cần id ổn định
    id: line.id ?? index,

    // Bảng trên màn tạo hiển thị serial CŨ ở cột "Serial", serial NCC trả về nằm ở
    // cột "Serial mới"; API trả serial hiện hành ở `serial` và serial cũ ở `oldSerial`.
    serial: line.oldSerial || line.serial,
    oldSerial: line.oldSerial,
    itemId: line.itemId,
    itemCode: line.itemCode,
    itemName: line.itemName,
    uomCode: line.uomCode,
    quantity: line.quantity,
    serialStatus: line.serialStatus,
    note: line.note,
    price: line.price,

    subInvId: line.subInvId,
    subInvCode: line.subInvCode,
    subInvName: line.subInvName,
    invOrgId: line.invOrgId,
    invOrgCode: line.invOrgCode,
    invOrgName: line.invOrgName,
    locatorId: line.locatorId,
    postionCode: line.postionCode,
    positionname: line.positionname,

    // Bảng trên màn tạo đọc nhà cung cấp ở attribute16.
    // Nhà cung cấp chỉ lấy từ header của phiếu, không đọc sang bảng khác.
    attribute16: detail.vendorCode || "",
    itemSource: line.itemSource,

    owWarrantyCode: line.owWarrantyCode,
    newSerial: line.newSerial,

    // Màn nhập BH NCC bind hình thức đổi qua formChange / attribute4 / attribute5.
    // attribute4 (mã sản phẩm mới) không lưu trên 2 bảng kho nên luôn rỗng — cột này
    // chỉ hiển thị khi formChange = DOI_MOI, giá trị API không bao giờ trả về.
    formChange: line.formChange || "KHONG_DOI",
    attribute4: null,
    attribute5: line.newSerial ?? "",
    parentSerial: line.childSerials?.[0]?.serial ?? "",
    childSerials: line.childSerials ?? [],
  }));
};

/**
 * Thông tin header dùng chung cho khối "Thông tin bên xuất / bên nhận",
 * đặt tên trường trùng với response của các API get-service-*-warranty-*
 * để màn tạo mới dùng lại được mà không phải đổi chỗ hiển thị.
 */
export const mapDetailToVoucherInfo = (detail: any) => ({
  transactionCode: detail?.transactionCode ?? "",
  createDate: detail?.createDate ?? null,
  creatorName:
    detail?.creatorCode && detail?.creatorName
      ? `${detail.creatorCode} - ${detail.creatorName}`
      : (detail?.creatorName ?? ""),
  departmentName: detail?.departmentName ?? "",
  branchName:
    detail?.branchCode && detail?.branchName
      ? `${detail.branchCode} - ${detail.branchName}`
      : (detail?.branchName ?? ""),
  inventoryName:
    detail?.inventoryCode && detail?.inventoryName
      ? `${detail.inventoryCode} - ${detail.inventoryName}`
      : (detail?.inventoryName ?? ""),
});
